import { useState, useCallback, type FormEvent } from 'react';
import { isValidEmail } from '@chikara/utils';
import { useSpinner } from '@chikara/ui';
import { passwordResetService } from '@/features/auth/services/passwordResetService';
import { t } from '@/i18n';

export const useForgotPassword = () => {
  const { show, hide, isSpinnerLoading } = useSpinner();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const isFormValid = isValidEmail(email) && !isSpinnerLoading;

  const validate = (): boolean => {
    if (!email) {
      setEmailError(t('forgotPassword.emailRequired'));
      return false;
    }
    if (!isValidEmail(email)) {
      setEmailError(t('forgotPassword.emailInvalid'));
      return false;
    }
    setEmailError(undefined);
    return true;
  };

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validate()) return;

    show();
    try {
      await passwordResetService.requestPasswordReset({ email: email.trim(), type: 'reset_password' });
      setSuccess(true);
    } catch {
      setApiError(t('forgotPassword.error'));
    } finally {
      hide();
    }
  }, [email, show, hide]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.replace(/\s/g, ''));
    if (emailError) setEmailError(undefined);
    if (apiError) setApiError(null);
  };

  return {
    email,
    emailError,
    success,
    apiError,
    isFormValid,
    isLoading: isSpinnerLoading,
    handleEmailChange,
    handleSubmit,
  };
};
